import type { FC } from 'react';
import { useEffect, useMemo } from 'react';

import { Box, Container } from '@mui/material';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { Outlet, useNavigate, useSearchParams } from 'react-router';

import { ExpiredBanner } from '@/components/ExpiredBanner';
import { useExpiredBannerContent } from '@/components/ExpiredBanner/hooks/useExpiredBannerContent';
import { ImpersonatorBanner } from '@/components/ImpersonatorBanner';
import LinkBar from '@/components/LinkBar';
import { NavigationMenu } from '@/components/NavigationMenu';
import UpgradeToPremiumButton from '@/components/UpgradeToPremium/UpgradeToPremiumButton';
import { VersionUpdateBanner } from '@/components/VersionUpdateBanner';
import { GX_AI_FLAG } from '@/constants/featureFlags';
import { Permission } from '@/entities/permissions';
import AskGXButton from '@/features/AIChat/AskGXButton';
import AskGXDrawer from '@/features/AIChat/AskGXDrawer';
import { useDashboardSync } from '@/features/Dashboard/hooks/useDashboardSync';
import { ExplainerVideoModal } from '@/features/ExplainerVideo/components/ExplainerVideoModal';
import { useExplainerVideoModal } from '@/features/ExplainerVideo/hooks/useExplainerVideoModal';
import FactsheetDialog from '@/features/Factsheet/components/FactsheetDialog';
import { FACTSHEET_PARAM_NAME } from '@/features/Factsheet/constants';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import useUserDetails from '@/hooks/useUserDetails';
import usePermission from '@/shared/hooks/usePermission';
import { closeDrawer, DrawerType, openDrawer } from '@/slices/drawerSlice';

/**
 * Main application layout with navigation, banners, and content area.
 *
 * Features:
 * - Persistent navigation menu (sidebar)
 * - Version update banner
 * - Expired subscription banner
 * - Upgrade to premium button (for basic users)
 * - AI chat integration (feature-flagged)
 * - Factsheet dialog handling
 * - Terms acceptance check and redirect
 * - Responsive container with max-width constraints
 *
 * This layout wraps all authenticated application pages.
 */
export const AppLayout: FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  useDashboardSync();
  const tripTokenState = useAppSelector(state => state.tripToken);
  const openDrawerType = useAppSelector(state => state.drawer.openDrawer);
  const { isUserExpired, impersonatorEmail } = useUserDetails();
  const { hasPermission } = usePermission();
  const expiredBannerContent = useExpiredBannerContent();
  const showUpgradeButton = useMemo(
    () =>
      isUserExpired !== undefined &&
      !hasPermission(Permission.DashboardPremiumWidgets) &&
      !expiredBannerContent,
    [expiredBannerContent, hasPermission, isUserExpired]
  );
  const showAskGXButton = useFeatureFlagEnabled(GX_AI_FLAG) && !isUserExpired;
  const { isOpen: isExplainerVideoOpen, onDismiss: onExplainerVideoDismiss } =
    useExplainerVideoModal();
  const [searchParams] = useSearchParams();

  const factsheetCode = searchParams.get(FACTSHEET_PARAM_NAME);

  useEffect(() => {
    if (searchParams.get('prompt')) {
      dispatch(openDrawer(DrawerType.ASK_GX));
    }
  }, [searchParams, dispatch]);

  useEffect(() => {
    //When impersonator prevents T&C acceptance
    if (impersonatorEmail) {
      return;
    }
    if (tripTokenState.status === 'fulfilled' && !tripTokenState.terms) {
      navigate('/welcome');
    }
  }, [tripTokenState.status, tripTokenState.terms, impersonatorEmail, navigate]);

  return (
    <Container
      maxWidth='xl'
      sx={theme => ({
        display: 'flex',
        height: '100vh',
        width: '100%',
        pr: '0!important',
        pl: '0!important',
        overflow: 'hidden',
        [theme.breakpoints.up('xl')]: {
          borderLeft: '1px solid #121836'
        }
      })}
    >
      <FactsheetDialog
        code={factsheetCode || ''}
        open={factsheetCode !== null}
      />
      <ExplainerVideoModal
        open={isExplainerVideoOpen}
        onClose={onExplainerVideoDismiss}
      />
      <NavigationMenu />
      <Box
        sx={{
          py: 3,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100vh',
          overflowY: 'auto',
          position: 'relative',
          boxSizing: 'border-box'
        }}
      >
        <VersionUpdateBanner />
        <ExpiredBanner />
        <Box
          sx={{
            px: 3
          }}
        >
          <ImpersonatorBanner />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <LinkBar />
            {showUpgradeButton && (
              <UpgradeToPremiumButton
                sx={{
                  height: '100%',
                  flexShrink: 0
                }}
                type='upgrade'
              />
            )}
            {showAskGXButton && (
              <>
                <AskGXButton
                  onClick={() => {
                    dispatch(openDrawer(DrawerType.ASK_GX));
                  }}
                />
                <AskGXDrawer
                  open={openDrawerType === DrawerType.ASK_GX}
                  onClose={e => {
                    e.stopPropagation();
                    dispatch(closeDrawer());
                  }}
                />
              </>
            )}
          </Box>
          <Outlet />
        </Box>
      </Box>
    </Container>
  );
};
